import { useState, useEffect, useCallback, useRef } from 'react';
import { getAnexosDepots } from '@spark/services';
import { useLatestAsyncRequest } from '@spark/ui-components/hooks/useLatestAsyncRequest';
import { formatarCNPJ, formatarDataSimples, toApiDate } from '@spark/utils/formatters';

const normalizeFilters = (filtros) => {
  return Object.fromEntries(
    Object.entries(filtros).filter(([, value]) => value !== undefined && value !== null && value !== '').map(
      ([key, value]) => [key, value instanceof Date ? toApiDate(value) : value]
    )
  );
};

export const useAnexosListagem = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [filtros, setFiltros] = useState({
    NumeroNF: '',
    SerieNF: '',
    CNPJCliente: '',
    TipoOperacao: '',
    NomeDestinatario: '',
    NomeRemetente: '',
    NomeUsuarioLeitura: '',
    NomeResponsavelComercial: '',
    NomeResponsavelLancamento: '',
    NomeResponsavelFechamento: '',
    ProtocoloPedidoTMSPaletizacao: '',
    ProtocoloPedidoTMSEtiquetagem: '',
    NumeroPedidoPaletizacao: '',
    NumeroPedidoEtiquetagem: '',
    Observacoes: '',
    NomeUsuarioEnvioCanhoto: '',
    DescricaoDivergencia: '',
    DataChegadaInicio: '',
    DataChegadaFim: '',
    DataLeituraInicio: '',
    DataLeituraFim: '',
    DataConferenciaInicio: '',
    DataConferenciaFim: '',
    DataArmazenagemInicio: '',
    DataArmazenagemFim: '',
    DataAgendamentoInicio: '',
    DataAgendamentoFim: '',
    DataSaidaInicio: '',
    DataSaidaFim: '',
    DataEnvioCanhotoInicio: '',
    DataEnvioCanhotoFim: '',
    MudancaTabelaPrecoManual: '',
    CobrancaPaletizacaoRealizada: '',
    CobrancaEtiquetagemRealizada: '',
    CobrancaArmazenagemRealizada: '',
    CobrancaArmazenagemPendente: '',
    ExisteDivergencia: '',
    Finalizado: '',
    PendenteEntrega: '',
    CanhotoEnviado: '',
    PendenteFinalizacao: '',
    OrdenarPor: '',
    Direcao: ''
  });

  const { beginRequest, isCurrentRequest, isMounted } = useLatestAsyncRequest();
  const didInitialLoadRef = useRef(false);
  const inFlightSignatureRef = useRef(null);

  const fetchData = useCallback(
    async (filtrosParaBuscar = filtros) => {
      if (!isMounted()) return;

      let requestId = null;

      try {
        const filters = {
          page: paginationState.pageIndex + 1,
          limit: paginationState.pageSize,
          ...normalizeFilters(filtrosParaBuscar)
        };

        const signature = JSON.stringify(filters);
        if (inFlightSignatureRef.current === signature) {
          return;
        }

        inFlightSignatureRef.current = signature;
        requestId = beginRequest();
        setLoading(true);

        const response = await getAnexosDepots(filters);
        if (!isCurrentRequest(requestId)) return;

        const responseData = response?.data || response || {};
        const registros = Array.isArray(responseData.registros) ? responseData.registros : [];

        const dadosProcessados = registros.map((item) => {
          const dataColetaDepotRaw =
            item.DataColetaDepot ||
            null;

          return ({
          id: item.Id ?? item.id,
          numeroNF: item.NomeOriginal || '',
          nomeOriginal: item.NomeOriginal || '',
          numeroContainer: item.Depot?.NumeroContainer || '',
          dataColetaDepot: dataColetaDepotRaw ? formatarDataSimples(dataColetaDepotRaw) : '',
          serieNF: '',
          numeroPedidoPaletizacao: '',
          numeroPedidoEtiquetagem: '',
          cnpjCliente: '',
          tipoOperacao: '',
          tabelaPreco: '',
          tabelaPrecoNome: '',
          idDepositoTabelaPreco: null,
          nomeDestinatario: '',
          nomeRemetente: '',
          nomeUsuarioLeitura: '',
          nomeResponsavelComercial: '',
          nomeResponsavelLancamento: '',
          nomeResponsavelFechamento: '',
          dataChegada: item.DataCriacao ? formatarDataSimples(item.DataCriacao) : '',
          dataCriacao: item.DataCriacao ? formatarDataSimples(item.DataCriacao) : '',
          dataLeitura: '',
          dataSaida: '',
          dataChegadaOriginal: item.DataCriacao || null,
          dataLeituraOriginal: null,
          dataConferenciaOriginal: null,
          dataArmazenagemOriginal: null,
          dataAgendamentoOriginal: null,
          dataSaidaOriginal: null,
          dataEnvioCanhotoOriginal: null,
          dataUltimaCobrancaArmazenagemOriginal: null,
          quantidadeTotalPaletes: null,
          quantidadeTotalEtiquetas: null,
          quantidadeTotalCaixas: null,
          valorUnPallet: null,
          valorUnEtiqueta: null,
          valorUnArmazenagemPallet: null,
          freetime: null,
          valorTotalPaletizacao: null,
          valorTotalEtiquetagem: null,
          valorTotalArmazenagemPallet: null,
          valorTotalEmitidoPaletizacao: null,
          valorTotalEmitidoEtiquetagem: null,
          usuarioEmissaoPaletizacao: '',
          usuarioEmissaoEtiquetagem: '',
          protocoloPedidoTMSPaletizacao: '',
          protocoloPedidoTMSEtiquetagem: '',
          observacoes: '',
          mudancaTabelaPrecoManual: false,
          urlImagemCanhoto: '',
          nomeUsuarioEnvioCanhoto: '',
          cobrancaPaletizacaoRealizada: null,
          cobrancaEtiquetagemRealizada: null,
          cobrancaArmazenagemRealizada: null,
          cobrancaArmazenagemPendente: null,
          existeDivergencia: null,
          descricaoDivergencia: '',
          finalizado: null,
          usuarioFinalizacao: '',
          pendenteFinalizacao: null,
          urlArquivo: item.UrlArquivo || '',
          urlAnexo: item.UrlArquivo || '',
          tamanhoArquivo: item.Tamanho ?? null,
          depotId: item.DepotId ?? null,
          dataColetaDepotOriginal: dataColetaDepotRaw,
        });
        });

        setData(dadosProcessados);
        setTotalRegistros(responseData.totalRegistros || dadosProcessados.length);
      } catch (error) {
        if (!requestId || !isCurrentRequest(requestId)) return;
        console.error('Erro ao buscar anexos de depot:', error);
      } finally {
        if (requestId && isCurrentRequest(requestId)) {
          setLoading(false);
          setFilterLoading(false);
          inFlightSignatureRef.current = null;
        }
      }
    },
    [beginRequest, filtros, isCurrentRequest, isMounted, paginationState.pageIndex, paginationState.pageSize]
  );

  useEffect(() => {
    if (!didInitialLoadRef.current) return;
    fetchData(filtros);
  }, [paginationState.pageIndex, paginationState.pageSize]);

  useEffect(() => {
    if (didInitialLoadRef.current) return;
    didInitialLoadRef.current = true;
    setLoading(true);
    fetchData();
  }, []);

  const handleFilterApply = useCallback(
    (newFilters) => {
      setFiltros(newFilters);
      setFilterLoading(true);
      setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
      fetchData(newFilters);
    },
    [fetchData]
  );

  const handleLimparFiltros = useCallback(
    (clearedFiltros) => {
      setFiltros(clearedFiltros);
      setLoading(true);
      setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
      fetchData(clearedFiltros);
    },
    [fetchData]
  );

  const handlePaginationChange = useCallback((next) => {
    setLoading(true);
    setPaginationState((prev) => {
      if (typeof next === 'function') return next(prev);
      if (typeof next === 'number') return { ...prev, pageIndex: next };
      if (next && typeof next === 'object') return { ...prev, ...next };
      return prev;
    });
  }, []);

  return {
    data,
    loading,
    filterLoading,
    totalRegistros,
    filtros,
    paginationState,
    fetchData,
    handleFilterApply,
    handleLimparFiltros,
    handlePaginationChange,
    setPaginationState
  };
};
